// Features contratadas do embarcador do user logado (PO1-1972 ↔ PO1-1974).
//
// Estratégia:
// - Só busca quando há user logado (depende de useAuth).
// - Refetch ao trocar embarcador via header (CompaniesProvider expõe `empresa`).
// - Exposto como Set<string> de códigos pra facilitar checagens.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { fetchMyFeatures } from "../services/featuresApi";
import { useAuth } from "./auth";
import { useCompanies } from "./companies";

const FeaturesContext = createContext({});

export const FeaturesProvider = ({ children }) => {
  const { user } = useAuth();
  const { empresa } = useCompanies();

  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // PO1-2713: true depois da PRIMEIRA carga (sucesso ou erro). O layout usa
  // isto para não disparar o fallback "/app/autopilot" antes das rotas
  // dependentes de feature existirem (senão um deep link como
  // ?abrir=<id> do e-mail da alçada perde a URL na carga fria).
  const [loadedOnce, setLoadedOnce] = useState(false);

  const load = useCallback(async () => {
    if (!user) {
      setFeatures([]);
      setLoadedOnce(true);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await fetchMyFeatures();
      setFeatures(list);
    } catch (err) {
      setError(err);
      setFeatures([]);
    } finally {
      setLoading(false);
      setLoadedOnce(true);
    }
  }, [user]);

  // Carrega ao logar e ao trocar embarcador (selectEmpresa do header).
  useEffect(() => {
    load();
    // empresa entra na deps porque admin Everlog troca embarcador via select
    // e as features contratadas mudam junto.
  }, [load, empresa]);

  const codes = new Set(features.map((f) => f.codigo));

  const value = {
    features,
    codes,
    loading,
    loadedOnce,
    error,
    refresh: load,
    hasFeature: (code) => codes.has(code),
  };

  return (
    <FeaturesContext.Provider value={value}>{children}</FeaturesContext.Provider>
  );
};

export const useFeatures = () => {
  const context = useContext(FeaturesContext);
  if (!context) {
    throw new Error("useFeatures must be used within a FeaturesProvider");
  }
  return context;
};
